import React, { useRef, useState, useCallback, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import { decoratedFetch } from "../../req_utils";
import { SUPPORT_EMAIL } from "../../constants";
import { useSelector } from "react-redux";
const DIVIDER_COLOR = "#D3D3D3";

const ProposalTemplateDialog = ({
  open,
  handleClose: handleCloseProp,
  refreshProposalTemplates,
  refreshProposalTemplatesAndClose,
  proposalTemplateId: proposalTemplateIdProp,
  setSnackbarOpen,
  setSnackbarMessage,
}) => {
  const employee = useSelector((state) => state.employee);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const [proposalTemplateId, setProposalTemplateId] = useState(
    proposalTemplateIdProp,
  );
  const [lastSavedName, setLastSavedName] = useState("");
  const [name, setName] = useState("");
  const [inclusions, setInclusions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [isItemized, setIsItemized] = useState(false);
  const [notes, setNotes] = useState([]);
  const [contractId, setContractId] = useState("");
  const [lastSavedContractId, setLastSavedContractId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedInclusions, setSelectedInclusions] = useState([]);
  const [selectedExclusions, setSelectedExclusions] = useState([]);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const inclusionsApiRef = useGridApiRef();
  const exclusionsApiRef = useGridApiRef();
  const notesApiRef = useGridApiRef();
  const enterPressedRef = useRef(false);
  const nameInputRef = useRef();
  const startEditModeOnLastRowRef = useRef(false);
  const [gridKey, setGridKey] = useState(0);
  const [recentlyAddedInclusionId, setRecentlyAddedInclusionId] = useState("");
  const [recentlyAddedExclusionId, setRecentlyAddedExclusionId] = useState("");
  const [recentlyAddedNoteId, setRecentlyAddedNoteId] = useState("");

  const descriptionColumns = [
    {
      field: "description",
      headerName: "Description",
      editable: true,
      flex: 1,
    },
  ];

  const itemizedColumns = [
    {
      field: "quantity",
      headerName: "Quantity",
      type: "number",
      editable: true,
      flex: 1,
    },
    {
      field: "unit",
      headerName: "Unit",
      editable: true,
      flex: 0.5,
    },
    {
      field: "category",
      headerName: "Category",
      editable: true,
      width: 175,
      type: "singleSelect",
      valueOptions: ["Material", "Freight", "Labor"],
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      editable: true,
      width: 380,
      flex: 3,
    },
    {
      field: "unit_price",
      headerName: "Unit Price",
      type: "number",
      editable: true,
      flex: 1,
    },
    {
      field: "extension",
      headerName: "Extension",
      type: "number",
      editable: false,
      flex: 1,
    },
  ];

  const updateProposalTemplateInclusions = ({
    inclusion_to_add = null,
    inclusion_to_update = null,
    inclusion_to_shift = null,
    inclusion_ids_to_delete = null,
  }) => {
    let body = {};
    if (inclusion_to_add) {
      body.inclusion_to_add = inclusion_to_add;
      delete body.inclusion_to_add.id;
      delete body.inclusion_to_add.api_id;
    }
    if (inclusion_to_update) {
      body.inclusion_to_update = {
        ...inclusion_to_update,
        id: inclusion_to_update.api_id,
      };
      delete body.inclusion_to_update.api_id;
    }
    if (inclusion_to_shift) {
      body.inclusion_to_shift = inclusion_to_shift;
    }
    if (inclusion_ids_to_delete) {
      body.inclusion_ids_to_delete = inclusion_ids_to_delete;
    }
    decoratedFetch(
      `/update_proposal_template_inclusions/${proposalTemplateId}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error message generated");
        }
        return response.json();
      })
      .then((data) => {
        if ("added_inclusion_id" in data) {
          setRecentlyAddedInclusionId(data.added_inclusion_id);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to save proposal template");
        console.error(`Error updating proposal template inclusions: ${error}`);
      });
  };

  const updateProposalTemplateExclusions = ({
    exclusion_to_add = null,
    exclusion_to_update = null,
    exclusion_to_shift = null,
    exclusion_ids_to_delete = null,
  }) => {
    let body = {};
    if (exclusion_to_add) {
      body.exclusion_to_add = exclusion_to_add;
      delete body.exclusion_to_add.id;
      delete body.exclusion_to_add.api_id;
    }
    if (exclusion_to_update) {
      body.exclusion_to_update = {
        ...exclusion_to_update,
        id: exclusion_to_update.api_id,
      };
      delete body.exclusion_to_update.api_id;
    }
    if (exclusion_to_shift) {
      body.exclusion_to_shift = exclusion_to_shift;
    }
    if (exclusion_ids_to_delete) {
      body.exclusion_ids_to_delete = exclusion_ids_to_delete;
    }
    decoratedFetch(
      `/update_proposal_template_exclusions/${proposalTemplateId}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error message generated");
        }
        return response.json();
      })
      .then((data) => {
        if ("added_exclusion_id" in data) {
          setRecentlyAddedExclusionId(data.added_exclusion_id);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to save proposal template");
        console.error(`Error updating proposal template exclusions: ${error}`);
      });
  };

  const updateProposalTemplateNotes = ({
    note_to_add = null,
    note_to_update = null,
    note_to_shift = null,
    note_ids_to_delete = null,
  }) => {
    let body = {};
    if (note_to_add) {
      body.note_to_add = note_to_add;
      delete body.note_to_add.id;
      delete body.note_to_add.api_id;
    }
    if (note_to_update) {
      body.note_to_update = { ...note_to_update, id: note_to_update.api_id };
      delete body.note_to_update.api_id;
    }
    if (note_to_shift) {
      body.note_to_shift = note_to_shift;
    }
    if (note_ids_to_delete) {
      body.note_ids_to_delete = note_ids_to_delete;
    }
    decoratedFetch(`/update_proposal_template_notes/${proposalTemplateId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error message generated");
        }
        return response.json();
      })
      .then((data) => {
        if ("added_note_id" in data) {
          setRecentlyAddedNoteId(data.added_note_id);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to save proposal template");
        console.error(`Error updating proposal template notes: ${error}`);
      });
  };

  const handleClose = (e, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    resetState();
    handleCloseProp();
  };

  const resetState = () => {
    setProposalTemplateId(null);
    setName("");
    setLastSavedName("");
    setInclusions([]);
    setExclusions([]);
    setIsItemized(false);
    setNotes([]);
    setContractId("");
    setLastSavedContractId("");
    setLoading(false);
    setFetchingData(false);
    setAlertMessage("");
    setSelectedInclusions([]);
    setSelectedExclusions([]);
    setSelectedNotes([]);
    setGridKey(0);
    setRecentlyAddedInclusionId("");
    setRecentlyAddedExclusionId("");
    setRecentlyAddedNoteId("");
  };

  const handleDelete = () => {
    setLoading(true);
    const url_suffix = `/delete_proposal_template/${proposalTemplateId}`;
    decoratedFetch(url_suffix, {
      method: "DELETE",
    })
      .then((response) => {
        if (response.status === 200) {
          setLoading(false);
          refreshProposalTemplatesAndClose();
          resetState();
          setSnackbarOpen(true);
          setSnackbarMessage(`Proposal template deleted`);
          return;
        } else {
          throw new Error("Error message generated");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setSnackbarOpen(true);
        setSnackbarMessage(`Failed to delete proposal template`);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    if (proposalTemplateIdProp) {
      setProposalTemplateId(proposalTemplateIdProp);
      setFetchingData(true);
      decoratedFetch(`/look_up_proposal_template?id=${proposalTemplateIdProp}`)
        .then((response) => response.json())
        .then((data) => {
          const inclusionsWithIds = (data?.inclusions || []).map(
            (inclusion, index) => ({
              id: index,
              api_id: inclusion.id || `temp_${index}`,
              quantity: inclusion.quantity || "",
              unit: inclusion.unit || "",
              category: inclusion.category || "",
              description: inclusion.description || inclusion,
              unit_price: inclusion.unit_price || "",
              extension: inclusion.extension || "",
            }),
          );

          const exclusionsWithIds = (data?.exclusions || []).map(
            (excl, index) => ({
              ...excl,
              api_id: excl.id,
              id: index,
            }),
          );

          const notesWithIds = (data?.notes || []).map((note, index) => ({
            ...note,
            api_id: note.id,
            id: index,
          }));

          setInclusions(inclusionsWithIds);
          setExclusions(exclusionsWithIds);
          setNotes(notesWithIds);
          setName(data?.name);
          setLastSavedName(data?.name);
          setContractId(data?.cmas_id);
          setLastSavedContractId(data?.cmas_id);
          setIsItemized(data?.is_itemized);
          setFetchingData(false);
        })
        .catch((error) => {
          console.error(`Error listing proposal templates: ${error}`);
          setFetchingData(false);
        });
    }
  }, [proposalTemplateIdProp]);

  const handleCreate = (event) => {
    event.preventDefault();
    setLoading(true);

    if (!name) {
      setAlertMessage("Template name is required.");
      setLoading(false);
      return;
    }
    decoratedFetch("/create_proposal_template", {
      method: "POST",
      body: JSON.stringify({ 
        name: name,
        branch_id: employee_branch_id,
      }),
    })
      .then((response) => {
        if (response.status === 201) {
          return response.json();
        }
        switch (response.status) {
          case 400:
            setAlertMessage("Template name is required.");
            break;
          case 500:
            setAlertMessage(
              `Internal server error. If this persists, please contact ${SUPPORT_EMAIL}`,
            );
            break;
          default:
            setAlertMessage(
              `Unknown error. If this persists, please contact ${SUPPORT_EMAIL}`,
            );
        }
        throw new Error();
      })
      .then((data) => {
        setLoading(false);
        refreshProposalTemplates();
        setLastSavedName(name);
        setProposalTemplateId(data.id);
        setSnackbarOpen(true);
        setSnackbarMessage("Proposal template created");
      })
      .catch((error) => {
        console.error(`Error creating proposal template: ${error}`);
        setLoading(false);
      });
  };

  const handleAddNoteRow = () => {
    const newRow = {
      id: notes.length,
      api_id: "temp_id",
      description: "",
    };
    setNotes((oldNotes) => {
      startEditModeOnLastRowRef.current = true;
      return [...oldNotes, newRow];
    });
    const noteToAdd = { ...newRow };
    noteToAdd.index_num = notes.length;
    updateProposalTemplateNotes({ note_to_add: noteToAdd });
  };

  const handleNoteRowDelete = () => {
    setNotes(
      notes
        .filter((row) => !selectedNotes.includes(row.id))
        .map((row, i) => ({ ...row, id: i })),
    );
    updateProposalTemplateNotes({
      note_ids_to_delete: selectedNotes.map(
        (id) => notes.find((note) => note.id === id)?.api_id,
      ),
    });
    setSelectedNotes([]);
  };

  const handleNoteRowSelectionModelChange = useCallback(
    (newSelectionModel) => {
      setSelectedNotes(newSelectionModel);
    },
    [setSelectedNotes],
  );

  const handleNoteEditCommit = (updatedRow) => {
    if (!updatedRow.description) {
      setNotes(
        notes
          .filter((note) => note.id !== updatedRow.id)
          .map((note, i) => ({ ...note, id: i })),
      );
      updateProposalTemplateNotes({
        note_ids_to_delete: [
          updatedRow.api_id === "temp_id"
            ? recentlyAddedNoteId
            : updatedRow.api_id,
        ],
      });
      enterPressedRef.current = false;
      return updatedRow;
    }
    let ops = {};
    const newRows = notes.map((row) => {
      if (row.id === updatedRow.id) {
        const api_id =
          updatedRow.api_id === "temp_id"
            ? recentlyAddedNoteId
            : updatedRow.api_id;
        const newRow = {
          ...row,
          ...updatedRow,
          api_id: api_id,
        };
        ops.note_to_update = { ...newRow };
        return newRow;
      }
      return row;
    });
    if (
      enterPressedRef.current &&
      updatedRow.id === notes[notes.length - 1].id
    ) {
      // If the user clicked "Enter" on the last row
      const newRow = {
        id: notes.length,
        api_id: "temp_id",
        description: "",
      };
      newRows.push(newRow);
      ops.note_to_add = { ...newRow };
      ops.note_to_add.index_num = notes.length;
    }
    enterPressedRef.current = false;
    setNotes((oldRows) => {
      startEditModeOnLastRowRef.current = true;
      return newRows;
    });
    updateProposalTemplateNotes(ops);
    return updatedRow;
  };

  const handleInclusionRowSelectionModelChange = useCallback(
    (newSelectionModel) => {
      setSelectedInclusions(newSelectionModel);
    },
    [setSelectedInclusions],
  );

  const handleInclusionEditCommit = (updatedRow) => {
    const isEmpty = !updatedRow.description?.trim();

    if (isEmpty) {
      // Update state by filtering out the empty row and re-indexing
      setInclusions((currentRows) =>
        currentRows
          .filter((row) => row.id !== updatedRow.id)
          .map((row, index) => ({
            ...row,
            id: index,
          })),
      );
      updateProposalTemplateInclusions({
        inclusion_ids_to_delete: [
          updatedRow.api_id === "temp_id"
            ? recentlyAddedInclusionId
            : updatedRow.api_id,
        ],
      });
      // Force grid re-render by updating the key
      setGridKey((prevKey) => prevKey + 1);
      // Return the row so the grid completes its commit cycle
      return updatedRow;
    }

    const api_id =
      updatedRow.api_id === "temp_id"
        ? recentlyAddedInclusionId
        : updatedRow.api_id;
    const quantity = parseFloat(updatedRow.quantity) || 0;
    const rawUnitPrice =
      parseFloat(
        (updatedRow.unit_price || "").toString().replace(/[^0-9.-]+/g, ""),
      ) || 0;

    const newRow = {
      ...updatedRow,
      api_id: api_id,
      unit_price: `$${rawUnitPrice.toFixed(2)}`,
      extension: `$${(quantity * rawUnitPrice).toFixed(2)}`,
    };

    const updatedInclusions = inclusions.map((row) =>
      row.id === updatedRow.id ? newRow : row,
    );
    let ops = {};
    ops.inclusion_to_update = { ...newRow };

    if (
      enterPressedRef.current &&
      updatedRow.id === inclusions[inclusions.length - 1]?.id
    ) {
      const blankRow = {
        id: inclusions.length,
        api_id: "temp_id",
        quantity: "",
        unit: "",
        category: "",
        description: "",
        unit_price: "",
        extension: "",
      };
      updatedInclusions.push(blankRow);
      ops.inclusion_to_add = { ...blankRow };
      ops.inclusion_to_add.index_num = inclusions.length;
    }
    enterPressedRef.current = false;
    setInclusions((oldInclusions) => {
      startEditModeOnLastRowRef.current = true;
      return updatedInclusions;
    });
    updateProposalTemplateInclusions(ops);
    return updatedRow;
  };

  const handleAddExclusionRow = () => {
    const newRow = {
      id: exclusions.length,
      api_id: "temp_id",
      description: "",
    };
    setExclusions((oldExclusions) => {
      startEditModeOnLastRowRef.current = true;
      return [...oldExclusions, newRow];
    });
    const exclusionToAdd = { ...newRow };
    exclusionToAdd.index_num = exclusions.length;
    updateProposalTemplateExclusions({ exclusion_to_add: exclusionToAdd });
  };

  const handleExclusionRowDelete = () => {
    setExclusions(
      exclusions
        .filter((row) => !selectedExclusions.includes(row.id))
        .map((row, i) => ({ ...row, id: i })),
    );
    updateProposalTemplateExclusions({
      exclusion_ids_to_delete: selectedExclusions.map(
        (id) => exclusions.find((excl) => excl.id === id)?.api_id,
      ),
    });
    setSelectedExclusions([]);
  };

  const handleExclusionRowSelectionModelChange = useCallback(
    (newSelectionModel) => {
      setSelectedExclusions(newSelectionModel);
    },
    [setSelectedExclusions],
  );

  const handleCellKeyDown = (params, event) => {
    if (event.key === "Enter") {
      enterPressedRef.current = true;
      return;
    }
    enterPressedRef.current = false;
  };

  const handleExclusionEditCommit = (updatedRow) => {
    if (!updatedRow.description) {
      setExclusions(
        exclusions
          .filter((excl) => excl.id !== updatedRow.id)
          .map((excl, i) => ({ ...excl, id: i })),
      );
      updateProposalTemplateExclusions({
        exclusion_ids_to_delete: [
          updatedRow.api_id === "temp_id"
            ? recentlyAddedExclusionId
            : updatedRow.api_id,
        ],
      });
      enterPressedRef.current = false;
      return updatedRow;
    }
    let ops = {};
    const newRows = exclusions.map((row) => {
      if (row.id === updatedRow.id) {
        const api_id =
          updatedRow.api_id === "temp_id"
            ? recentlyAddedExclusionId
            : updatedRow.api_id;
        const newRow = {
          ...row,
          ...updatedRow,
          api_id: api_id,
        };
        ops.exclusion_to_update = { ...newRow };
        return newRow;
      }
      return row;
    });
    if (
      enterPressedRef.current &&
      updatedRow.id === exclusions[exclusions.length - 1].id
    ) {
      // If the user clicked "Enter" on the last row
      const newRow = {
        id: exclusions.length,
        api_id: "temp_id",
        description: "",
      };
      newRows.push(newRow);
      ops.exclusion_to_add = { ...newRow };
      ops.exclusion_to_add.index_num = exclusions.length;
    }
    enterPressedRef.current = false;
    setExclusions((oldRows) => {
      startEditModeOnLastRowRef.current = true;
      return newRows;
    });
    updateProposalTemplateExclusions(ops);
    return updatedRow;
  };

  // This effect is just to focus on the last row's description column when enter was recently pressed on the last row
  useEffect(() => {
    if (
      startEditModeOnLastRowRef.current &&
      inclusions.length &&
      inclusionsApiRef?.current &&
      inclusions[inclusions.length - 1]
    ) {
      const lastRowId = inclusions[inclusions.length - 1].id;
      try {
        inclusionsApiRef.current.setCellFocus(lastRowId, "description");
        inclusionsApiRef.current.startCellEditMode({
          id: lastRowId,
          field: "description",
        });
      } catch (e) {
        console.warn("Row not found during focus attempt", e);
      }
      startEditModeOnLastRowRef.current = false;
    }
  }, [inclusionsApiRef, inclusions]);

  // This effect is just to focus on the last row's description column when enter was recently pressed on the last row
  useEffect(() => {
    if (
      startEditModeOnLastRowRef.current &&
      exclusions.length &&
      exclusionsApiRef?.current &&
      Object.keys(exclusionsApiRef.current).length !== 0
    ) {
      exclusionsApiRef.current.setCellFocus(
        exclusions.length - 1,
        "description",
      );
      exclusionsApiRef.current.startCellEditMode({
        id: exclusions.length - 1,
        field: "description",
      });
      startEditModeOnLastRowRef.current = false;
    }
  }, [exclusionsApiRef, exclusions]);

  // This effect is just to focus on the last row's description column when enter was recently pressed on the last row
  useEffect(() => {
    if (
      startEditModeOnLastRowRef.current &&
      notes.length &&
      notesApiRef?.current &&
      Object.keys(notesApiRef.current).length !== 0
    ) {
      notesApiRef.current.setCellFocus(notes.length - 1, "description");
      notesApiRef.current.startCellEditMode({
        id: notes.length - 1,
        field: "description",
      });
      startEditModeOnLastRowRef.current = false;
    }
  }, [notesApiRef, notes]);

  const handleAddInclusionRow = () => {
    const newRow = {
      id: inclusions.length,
      api_id: "temp_id",
      quantity: "",
      unit: "",
      category: "",
      description: "",
      unit_price: "",
      extension: "",
    };
    setInclusions((oldInclusions) => {
      startEditModeOnLastRowRef.current = true;
      return [...oldInclusions, newRow];
    });
    const inclusionToAdd = { ...newRow };
    inclusionToAdd.index_num = inclusions.length;
    updateProposalTemplateInclusions({ inclusion_to_add: inclusionToAdd });
  };

  const handleInclusionRowDelete = () => {
    setInclusions(
      inclusions
        .filter((row) => !selectedInclusions.includes(row.id))
        .map((row, i) => ({ ...row, id: i })),
    );
    updateProposalTemplateInclusions({
      inclusion_ids_to_delete: selectedInclusions.map(
        (id) => inclusions.find((incl) => incl.id === id)?.api_id,
      ),
    });
    setSelectedInclusions([]);
  };

  const handleTemplateNameKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCreate(e);
      nameInputRef.current?.blur();
    }
  };

  const handleSave = (payload) => {
    if (!proposalTemplateId) {
      return;
    }
    setLoading(true);

    if ("name" in payload && !payload.name) {
      setAlertMessage("Template name is required.");
      setLoading(false);
      return;
    }
    decoratedFetch(`/update_proposal_template/${proposalTemplateId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.status === 200) {
          return;
        }
        switch (response.status) {
          case 400:
            setAlertMessage("Template name is required.");
            break;
          case 500:
            setAlertMessage(
              `Internal server error. If this persists, please contact ${SUPPORT_EMAIL}`,
            );
            break;
          default:
            setAlertMessage(
              `Unknown error. If this persists, please contact ${SUPPORT_EMAIL}`,
            );
        }
        throw new Error();
      })
      .then(() => {
        if ("name" in payload) {
          refreshProposalTemplates();
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Error editing proposal template: ${error}`);
        setLoading(false);
      });
  };

  const onNameBlur = () => {
    if (!proposalTemplateId || name === lastSavedName) {
      return;
    }
    handleSave({ name: name });
    setLastSavedName(name);
  };

  const onContractIdBlur = () => {
    if (contractId === lastSavedContractId) {
      return;
    }
    handleSave({ cmas_id: contractId });
    setLastSavedContractId(contractId);
  };

  const onItemizedClicked = (val) => {
    setIsItemized(val);
    handleSave({ is_itemized: val });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      disableEscapeKeyDown
      disableScrollLock
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "5px",
          }}
        >
          <div>{`${proposalTemplateId ? "Edit" : "Create"} Proposal Template`}</div>
          <div>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "white",
          }}
        >
          {alertMessage && (
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Alert
                onClose={() => setAlertMessage("")}
                severity={"error"}
                sx={{
                  width: "80%",
                  borderRadius: 8,
                  marginBottom: "15px",
                  marginTop: "4px",
                }}
              >
                {alertMessage}
              </Alert>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sx={{ mt: "10px", mb: proposalTemplateId ? "16px" : "0px" }}
          >
            <TextField
              label="Template Name"
              variant="outlined"
              autoFocus={!proposalTemplateId}
              fullWidth
              size="small"
              value={name}
              onKeyDown={(e) =>
                !proposalTemplateId && handleTemplateNameKeyDown(e)
              }
              inputRef={nameInputRef}
              onChange={(e) => setName(e.target.value)}
              onBlur={onNameBlur}
            />
          </Grid>
        </Grid>
        {proposalTemplateId ? (
          <Grid container>
            <Grid container spacing={1}>
              <Grid
                item
                xs={12}
                sx={{
                  mt: "10px",
                  display: "flex",
                }}
              >
                <TextField
                  label="Contract ID"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                  onBlur={(e) => onContractIdBlur(e.target.value)}
                />
                <FormControlLabel
                  sx={{ ml: "8px" }}
                  control={
                    <Checkbox
                      checked={isItemized}
                      onChange={(e) => onItemizedClicked(e.target.checked)}
                    />
                  }
                  label="Itemize"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  position: "sticky",
                  top: "60px",
                  zIndex: 100,
                  backgroundColor: "white",
                  mt: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontSize: "1.2rem" }}>Inclusions</Typography>
                {fetchingData ? (
                  <Grid container justifyContent="flex-end">
                    <Grid item>
                      <CircularProgress
                        size="22px"
                        sx={{ mr: "8px", mt: "12px", mb: "-1px" }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container justifyContent="flex-end">
                    <Grid
                      item
                      sx={{
                        marginRight: "4px",
                        visibility: selectedInclusions.length
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      <IconButton
                        aria-label="remove-inclusion"
                        onClick={handleInclusionRowDelete}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton
                        aria-label="add-inclusion"
                        onClick={handleAddInclusionRow}
                      >
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} sx={{ mb: "11px", width: "100%" }}>
                {inclusions.length ? (
                  <DataGridPro
                    key={gridKey}
                    apiRef={inclusionsApiRef}
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        display: isItemized ? "block" : "none",
                      },
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cellCheckbox:focus, & .MuiDataGrid-cellCheckbox:focus-within":
                        {
                          outline: "none",
                        },
                    }}
                    hideFooter
                    rows={inclusions}
                    columns={isItemized ? itemizedColumns : descriptionColumns}
                    processRowUpdate={handleInclusionEditCommit}
                    onProcessRowUpdateError={(error) => console.error(error)}
                    experimentalFeatures={{ newEditingApi: true }}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelectionModel) =>
                      handleInclusionRowSelectionModelChange(newSelectionModel)
                    }
                    rowSelectionModel={selectedInclusions}
                    getRowId={(row) => row.id}
                    onCellClick={(params, e) => e.stopPropagation()}
                    localeText={{ noRowsLabel: "No Inclusions" }}
                    onCellKeyDown={handleCellKeyDown}
                    rowReordering
                    onRowOrderChange={(e) => {
                      let mutableInclusions = [...inclusions];
                      const sourceRow = mutableInclusions[e.oldIndex];
                      mutableInclusions.splice(e.oldIndex, 1);
                      mutableInclusions.splice(e.targetIndex, 0, sourceRow);
                      setInclusions(mutableInclusions);
                      updateProposalTemplateInclusions({
                        inclusion_to_shift: {
                          id: sourceRow.api_id,
                          target_idx: e.targetIndex,
                        },
                      });
                    }}
                  />
                ) : null}
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  position: "sticky",
                  top: "60px",
                  zIndex: 100,
                  backgroundColor: "white",
                  mt: "0px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontSize: "1.2rem" }}>Exclusions</Typography>
                {fetchingData ? (
                  <Grid container justifyContent="flex-end">
                    <Grid item>
                      <CircularProgress
                        size="22px"
                        sx={{ mr: "8px", mt: "12px", mb: "-1px" }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container justifyContent="flex-end">
                    <Grid
                      item
                      sx={{
                        marginRight: "4px",
                        visibility: selectedExclusions.length
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      <IconButton
                        aria-label="remove-exclusion-item"
                        onClick={handleExclusionRowDelete}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton
                        aria-label="add-exclusion-item"
                        onClick={handleAddExclusionRow}
                      >
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} sx={{ mb: "11px", width: "100%" }}>
                {exclusions.length ? (
                  <DataGridPro
                    apiRef={exclusionsApiRef}
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        display: "none",
                      },
                    }}
                    hideFooter
                    rows={exclusions}
                    columns={descriptionColumns}
                    processRowUpdate={handleExclusionEditCommit}
                    onProcessRowUpdateError={(error) => console.error(error)}
                    experimentalFeatures={{ newEditingApi: true }}
                    checkboxSelection
                    onRowSelectionModelChange={
                      handleExclusionRowSelectionModelChange
                    }
                    rowSelectionModel={selectedExclusions}
                    getRowId={(row) => row.id}
                    onCellClick={(params, e) => e.stopPropagation()}
                    onCellKeyDown={handleCellKeyDown}
                    localeText={{ noRowsLabel: "No Exclusions" }}
                    rowReordering
                    onRowOrderChange={(e) => {
                      let mutableExclusions = [...exclusions];
                      const sourceRow = mutableExclusions[e.oldIndex];
                      mutableExclusions.splice(e.oldIndex, 1);
                      mutableExclusions.splice(e.targetIndex, 0, sourceRow);
                      setExclusions(mutableExclusions);
                      updateProposalTemplateExclusions({
                        exclusion_to_shift: {
                          id: sourceRow.api_id,
                          target_idx: e.targetIndex,
                        },
                      });
                    }}
                  />
                ) : null}
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  position: "sticky",
                  top: "60px",
                  zIndex: 100,
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontSize: "1.2rem" }}>Notes</Typography>
                {fetchingData ? (
                  <Grid container justifyContent="flex-end">
                    <Grid item>
                      <CircularProgress
                        size="22px"
                        sx={{ mr: "8px", mt: "12px", mb: "-1px" }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container justifyContent="flex-end">
                    <Grid
                      item
                      sx={{
                        marginRight: "4px",
                        visibility: selectedNotes.length ? "visible" : "hidden",
                      }}
                    >
                      <IconButton
                        aria-label="remove-description-item"
                        onClick={handleNoteRowDelete}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton
                        aria-label="add-description-item"
                        onClick={handleAddNoteRow}
                      >
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} sx={{ mb: "11px", width: "100%" }}>
                {notes.length ? (
                  <DataGridPro
                    apiRef={notesApiRef}
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        display: "none",
                      },
                    }}
                    hideFooter
                    rows={notes}
                    columns={descriptionColumns}
                    processRowUpdate={handleNoteEditCommit}
                    onProcessRowUpdateError={(error) => console.error(error)}
                    experimentalFeatures={{ newEditingApi: true }}
                    checkboxSelection
                    onRowSelectionModelChange={
                      handleNoteRowSelectionModelChange
                    }
                    rowSelectionModel={selectedNotes}
                    getRowId={(row) => row.id}
                    onCellKeyDown={handleCellKeyDown}
                    rowReordering
                    onRowOrderChange={(e) => {
                      let mutableNotes = [...notes];
                      const sourceRow = mutableNotes[e.oldIndex];
                      mutableNotes.splice(e.oldIndex, 1);
                      mutableNotes.splice(e.targetIndex, 0, sourceRow);
                      setNotes(
                        mutableNotes.map((note, i) => ({ ...note, id: i })),
                      );
                      updateProposalTemplateNotes({
                        note_to_shift: {
                          id: sourceRow.api_id,
                          target_idx: e.targetIndex,
                        },
                      });
                    }}
                    localeText={{ noRowsLabel: "No Notes" }}
                  />
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ pt: "15px" }}>
        {proposalTemplateId ? (
          <Button
            sx={{ mr: "10px" }}
            variant="contained"
            color="error"
            onClick={handleDelete}
          >
            Delete
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{ mr: "10px" }}
          >
            Create
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProposalTemplateDialog;
